import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { ArrowRight, Newspaper } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

interface NewsItem {
  title: string;
  image: string;
  link: string;
}

const NewsSection = () => {
  const { data: newsData, isLoading } = useQuery({
    queryKey: ['news'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('fetch-news');
      
      if (error) {
        console.error('Error fetching news:', error);
        throw error;
      }
      
      return data;
    },
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
    retry: 2,
  });

  const newsItems: NewsItem[] = newsData?.news || [];
  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-secondary mb-4">
            Nos actus
          </h2>
          <p className="text-xl text-primary">
            Ça bouge dans nos agences !
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {isLoading ? (
            // Loading skeletons
            Array.from({ length: 3 }).map((_, index) => (
              <Card key={index} className="overflow-hidden border-none shadow-elegant bg-card">
                <Skeleton className="aspect-[4/3] w-full" />
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-24 mb-3" />
                  <Skeleton className="h-6 w-full mb-2" />
                  <Skeleton className="h-6 w-3/4" />
                </CardContent>
                <CardFooter className="px-6 pb-6">
                  <Skeleton className="h-4 w-32" />
                </CardFooter>
              </Card>
            ))
          ) : (
            newsItems.map((item, index) => (
              <Card key={index} className="overflow-hidden hover-lift border-none shadow-elegant bg-card">
                <a href={item.link} target="_blank" rel="noopener noreferrer" className="block">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-smooth hover:scale-105"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground leading-tight mb-4">
                      {item.title}
                    </h3>
                  </CardContent>
                  <CardFooter className="px-6 pb-6">
                    <Button variant="link" className="p-0 min-h-[24px] text-primary hover:text-primary/80">
                      En savoir plus
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardFooter>
                </a>
              </Card>
            ))
          )}
        </div>

        <div className="text-center mt-12">
          <Button variant="default" size="lg" className="px-8" asChild>
            <a href="https://www.lavarappe.fr/actualites/ressources-competences" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
              Voir toutes les actus
              <Newspaper className="h-5 w-5" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
